/**
 * Payslip print helper.
 *
 * Opens an A4-sized HTML window with one or many payslips, auto-fires
 * the browser print dialog, and lets the user save as PDF using their
 * native print-to-PDF action. No client-side PDF library — keeps the
 * bundle small and Indian-rupee glyphs render correctly.
 */

export interface PayslipBlock {
  employeeCode: string;
  employeeName: string;
  designation?: string;
  department?: string;
  pan?: string;
  uan?: string;
  bankAccount?: string;
  ifsc?: string;
  period: string; // e.g. "May 2026"
  daysWorked?: number;
  daysInMonth?: number;
  earnings: { name: string; amount: number }[];
  deductions: { name: string; amount: number }[];
}

export function printPayslips(slips: PayslipBlock[], tenantName = 'KSS BillBook'): void {
  if (slips.length === 0) return;
  const win = window.open('', '_blank', 'width=900,height=1100');
  if (!win) return;
  const body = slips.map((s) => slipHtml(s, tenantName)).join('<div class="pageBreak"></div>');
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Payslips</title>
<style>
  @page { size: A4; margin: 12mm; }
  body { font-family: ui-sans-serif, system-ui, sans-serif; margin: 0; color: #0f172a; }
  .pageBreak { page-break-after: always; }
  .slip { padding: 14mm; max-width: 186mm; margin: 0 auto; }
  h1 { font-size: 18px; margin: 0 0 4px; text-align: center; }
  .meta { font-size: 11px; color: #64748b; text-align: center; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 24px; margin-top: 10px; }
  .field { font-size: 11px; }
  .field b { color: #0f172a; }
  table { width: 100%; border-collapse: collapse; margin-top: 14px; font-size: 12px; }
  th, td { padding: 6px 8px; border: 1px solid #cbd5e1; }
  thead th { background: #f1f5f9; text-align: left; }
  tfoot td { font-weight: 700; background: #f8fafc; }
  .right { text-align: right; }
  .total { font-size: 14px; font-weight: 700; }
  .net { font-size: 16px; font-weight: 700; }
  .footnote { margin-top: 16px; font-size: 10px; color: #64748b; text-align: center; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head><body>${body}<script>window.onload=()=>window.print();</script></body></html>`);
  win.document.close();
}

function slipHtml(s: PayslipBlock, tenantName: string): string {
  const totalEarnings = s.earnings.reduce((sum, e) => sum + e.amount, 0);
  const totalDeductions = s.deductions.reduce((sum, d) => sum + d.amount, 0);
  const net = totalEarnings - totalDeductions;

  const earnRows = zipRows(s.earnings, s.deductions)
    .map(([e, d]) => `
      <tr>
        <td>${e ? escapeHtml(e.name) : ''}</td>
        <td class="right">${e ? inr(e.amount) : ''}</td>
        <td>${d ? escapeHtml(d.name) : ''}</td>
        <td class="right">${d ? inr(d.amount) : ''}</td>
      </tr>`)
    .join('');

  return `<div class="slip">
    <h1>${escapeHtml(tenantName)}</h1>
    <div class="meta">Payslip for ${escapeHtml(s.period)}</div>
    <div class="grid">
      <div class="field"><b>Employee:</b> ${escapeHtml(s.employeeName)} (${escapeHtml(s.employeeCode)})</div>
      <div class="field"><b>Designation:</b> ${escapeHtml(s.designation ?? '—')}</div>
      <div class="field"><b>Department:</b> ${escapeHtml(s.department ?? '—')}</div>
      <div class="field"><b>PAN:</b> ${escapeHtml(s.pan ?? '—')}</div>
      <div class="field"><b>UAN:</b> ${escapeHtml(s.uan ?? '—')}</div>
      <div class="field"><b>Bank:</b> ${escapeHtml(s.bankAccount ?? '—')}${s.ifsc ? ` / ${escapeHtml(s.ifsc)}` : ''}</div>
      <div class="field"><b>Days worked:</b> ${s.daysWorked ?? '—'}${
        s.daysInMonth ? ` / ${s.daysInMonth}` : ''
      }</div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Earnings</th><th class="right">Amount (₹)</th>
          <th>Deductions</th><th class="right">Amount (₹)</th>
        </tr>
      </thead>
      <tbody>${earnRows}</tbody>
      <tfoot>
        <tr>
          <td class="total">Gross earnings</td>
          <td class="right total">${inr(totalEarnings)}</td>
          <td class="total">Total deductions</td>
          <td class="right total">${inr(totalDeductions)}</td>
        </tr>
        <tr>
          <td colspan="3" class="net">Net pay</td>
          <td class="right net">₹ ${inr(net)}</td>
        </tr>
      </tfoot>
    </table>
    <div class="footnote">Computer-generated payslip. Signature not required.</div>
  </div>`;
}

function zipRows<E, D>(earnings: E[], deductions: D[]): [E | undefined, D | undefined][] {
  const rows: [E | undefined, D | undefined][] = [];
  const max = Math.max(earnings.length, deductions.length);
  for (let i = 0; i < max; i++) {
    rows.push([earnings[i], deductions[i]]);
  }
  return rows;
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
